/**
 * Created: 2026-04-07
 * Initial commit: Add formatEventTimeRange date utility
 *
 * Shared date formatting utilities for the frontend.
 */

import { addHours, format, getDay } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import { Weekday } from '../gql/graphql'

/**
 * Maps backend weekday index (0=Mon…6=Sun) to the GraphQL Weekday enum value.
 * date-fns getDay() returns 0=Sun…6=Sat, so convert with (getDay + 6) % 7.
 */
export const WEEKDAY_ENUM: Weekday[] = [
  Weekday.Monday,
  Weekday.Tuesday,
  Weekday.Wednesday,
  Weekday.Thursday,
  Weekday.Friday,
  Weekday.Saturday,
  Weekday.Sunday,
]

/**
 * Convert a Date to the GraphQL Weekday enum value using backend convention (0=Mon).
 */
export function dateToWeekday(date: Date): Weekday {
  return WEEKDAY_ENUM[(getDay(date) + 6) % 7]
}

/**
 * Get weekday text in Chinese format (周一, 周二, etc.)
 * Accepts a Date or a date string in YYYY-MM-DD format.
 */
export function getWeekdayText(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(`${date}T00:00:00`) : date
  return format(d, 'EEE', { locale: zhCN })
}

/**
 * Format a start time and duration into a human-readable range string.
 * Example: '2026-04-14 周二 15:00-16:00'
 */
export function formatEventTimeRange(
  startTime: string | Date,
  duration: number,
): string {
  const start = new Date(startTime)
  const end = addHours(start, duration)
  return `${format(start, 'yyyy-MM-dd EEE HH:mm', { locale: zhCN })}-${format(end, 'HH:mm')}`
}
